import { getContrastingColor, hasMinimumContrast } from '../utils/colorContrast';

import { FooterReferralLink } from '../footerReferralLink';
import { BeehiivLogo } from './BeehiivLogo';
import {
  absoluteWrapper,
  container,
  copyrightSection,
  copyrightText,
  linksSection,
  poweredByBadge,
  poweredByLogo,
} from './ProprietaryFooter.css';
import type { ProprietaryFooterData, ProprietaryFooterProps } from './ProprietaryFooter.type';

interface PoweredByBadgeProps {
  publicationName: string;
  publicationLogoUrl?: string | null;
  publicationUtmSource?: string | null;
  text: string;
  color: string;
}

const PoweredByBadge = ({
  publicationName,
  publicationLogoUrl,
  publicationUtmSource,
  text,
  color,
}: PoweredByBadgeProps) => {
  return (
    <FooterReferralLink
      publicationName={publicationName}
      publicationLogoUrl={publicationLogoUrl}
      utmSource={publicationUtmSource}
      className={poweredByBadge}
      style={{
        borderColor: color,
        color,
      }}
    >
      <BeehiivLogo className={poweredByLogo} />
      <span>{text}</span>
    </FooterReferralLink>
  );
};

interface ProprietaryFooterCoreProps {
  backgroundColor?: string;
  textColor?: string;
  data: ProprietaryFooterData;
  showPoweredByBeehiiv: boolean;
  shouldRenderOverContent: boolean;
}

const ProprietaryFooterCore = ({
  backgroundColor,
  textColor,
  data,
  showPoweredByBeehiiv,
  shouldRenderOverContent,
}: ProprietaryFooterCoreProps) => {
  const effectiveTextColor =
    textColor && hasMinimumContrast(textColor, backgroundColor) ? textColor : getContrastingColor(backgroundColor);

  const { translations, publicationName, publicationLogoUrl, publicationUtmSource } = data;

  const body = (
    <div className={container} style={{ backgroundColor: shouldRenderOverContent ? 'transparent' : backgroundColor }}>
      <div className={copyrightSection}>
        <span className={copyrightText} style={{ color: effectiveTextColor }}>
          {translations.copyrightText}
        </span>
      </div>

      <div className={linksSection}>
        {showPoweredByBeehiiv && (
          <PoweredByBadge
            publicationName={publicationName}
            publicationLogoUrl={publicationLogoUrl}
            publicationUtmSource={publicationUtmSource}
            text={translations.poweredByBeehiiv}
            color={effectiveTextColor}
          />
        )}
      </div>
    </div>
  );

  return shouldRenderOverContent ? <div className={absoluteWrapper}>{body}</div> : body;
};

/**
 * ProprietaryFooter - For direct usage (props-based rendering)
 * Use this in orchid-v2 and Dream Builder where data comes from context/hooks
 */
export const ProprietaryFooter = ({
  backgroundColor,
  textColor,
  data,
  showPoweredByBeehiiv = true,
  shouldRenderOverContent = false,
}: ProprietaryFooterProps) => {
  if (!data?.showBeehiivBranding) return null;

  return (
    <ProprietaryFooterCore
      backgroundColor={backgroundColor}
      textColor={textColor}
      data={data}
      showPoweredByBeehiiv={showPoweredByBeehiiv}
      shouldRenderOverContent={shouldRenderOverContent}
    />
  );
};
